import { Role, rolesManipulator, useGetAccessTokenPayload, useGetUserInfo, useLogout } from 'auth';
import { ChangeEventHandler, useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { useCurrentUserQuery } from '@/modules/auth/queries/user.tsx';
import { checkAccessEnv } from '@/modules/auth/utils/checkAccessEnv.ts';
import {
  useReset2FA,
  useToggle2FA,
  useUpdateUserIdentityCenterProfile,
  useUpdateUserSRProfile,
} from '@/modules/common/queries/profileSettings.ts';
import { Avatar, Button, Toggle, TooltipNew } from 'ui';
import { AlertOrangeIcon } from 'ui/icons';

export const ProfileTabContent = () => {
  const { avatar, last_name, first_name, email } = useGetUserInfo();
  const { isEnable2FA, refresh } = useGetAccessTokenPayload();
  const { data: currentUser } = useCurrentUserQuery();
  const isInvestor = rolesManipulator.current_user_role === Role.SR_INVESTOR;
  const logout = useLogout();
  const [isToggle2FA, setIsToggle2FA] = useState(false);

  const handleOnSuccessReset2FA = useCallback(async () => {
    setIsToggle2FA(true);
    toast(
      t => (
        <span className="flex flex-col justify-center items-center gap-2 text-sm">
          <span>
            <img src={AlertOrangeIcon} className="w-8 h-8" alt="alert" />
          </span>
          <div>
            To set up your new 2FA, please log out and log in again to see a 2FA set up screen.
          </div>
          <button
            className="px-2 py-1 border rounded !hover:!border-blue-800"
            onClick={() => {
              toast.dismiss(t.id);
              logout();
            }}
          >
            Logout
          </button>
        </span>
      ),
      {
        duration: 10000,
        id: 'reset',
      },
    );
    await refresh();
    setIsToggle2FA(false);
  }, [logout, refresh]);

  const { mutateAsync: reset2FA, isLoading: isLoadingReset2FA } = useReset2FA({
    onSuccess: handleOnSuccessReset2FA,
  });

  const handleOnSuccessToggle2FA = useCallback(
    async (enabled: boolean) => {
      setIsToggle2FA(true);
      if (enabled) {
        reset2FA();
      } else {
        toast.dismiss('reset');
        await refresh();
      }
      setIsToggle2FA(false);
    },
    [refresh, reset2FA],
  );

  const { mutateAsync: toggle2FA, isLoading: isLoadingToggle2FA } = useToggle2FA({
    onSuccess: handleOnSuccessToggle2FA,
  });
  const { mutateAsync: updateSRProfile, isLoading: isUpdatingSRProfile } = useUpdateUserSRProfile();
  const { mutateAsync: updateIdentityCenterProfile, isLoading: isUpdatingIdentityCEnterProfile } =
    useUpdateUserIdentityCenterProfile();

  const isLoading = isUpdatingSRProfile || isUpdatingIdentityCEnterProfile;

  const handleToggleNotifications = useCallback(() => {
    updateSRProfile({ notifications_enabled: !currentUser?.notifications_enabled });
  }, [currentUser?.notifications_enabled, updateSRProfile]);

  const handleLoadFile: ChangeEventHandler<HTMLInputElement> = useCallback(
    event => {
      const file = event.target.files?.[0];

      if (!file) return;

      const reader = new FileReader();

      reader.onload = function () {
        if (typeof reader.result === 'string') {
          updateIdentityCenterProfile({ avatar: reader.result });
        }
      };
      reader.onerror = function (error) {
        console.log('Error: ', error);
      };

      reader.readAsDataURL(file);
    },
    [updateIdentityCenterProfile],
  );

  const handleRemoveAvatar = useCallback(() => {
    updateIdentityCenterProfile({ avatar: null });
  }, [updateIdentityCenterProfile]);

  const handleReset2fa = () => {
    reset2FA();
  };

  const handleToggle2FA = useCallback(() => {
    toggle2FA({ enabled: !isEnable2FA });
  }, [isEnable2FA, toggle2FA]);

  const get2FADisabledState = (isEnabled: boolean): boolean => {
    // DEV & STAGING → you can always change
    if (checkAccessEnv(['dev', 'staging'])) {
      return false;
    }

    // PRODUCTION → can be turned on, but not turned off
    // if disabled, allow clicks
    if (!isEnabled) return false;

    // if enabled, block (cannot be disabled)
    return true;
  };

  return (
    <div className="flex flex-col gap-10 w-full">
      <div className="flex flex-col gap-1">
        <span className="font-semibold font-[Inter]">Email</span>
        <div className="text-[15px] leading-5">{email}</div>
      </div>

      <div className="flex flex-col gap-6">
        <span className="font-semibold font-[Inter]">Avatar</span>
        <div className="flex gap-6 items-center justify-between w-full">
          <div>
            <Avatar
              className="w-[56px] h-[56px]"
              src={avatar}
              firstName={first_name}
              lastName={last_name}
            />
          </div>

          <div className="flex gap-6 items-center">
            <div className="w-fit">
              <Button variant="plain" className="cursor-pointer !p-[0px]" disabled={isLoading}>
                <label
                  htmlFor="upload-photo"
                  className="cursor-pointer flex items-center justify-center py-[9px] px-[16px] w-[100%] h-[100%] m-0"
                >
                  {avatar ? 'Edit' : 'Add photo'}
                </label>
                <input
                  type="file"
                  name="photo"
                  id="upload-photo"
                  accept="image/png, image/jpg, image/jpeg"
                  className="w-0 h-0 opacity-0 cursor-pointer"
                  onChange={handleLoadFile}
                />
              </Button>
            </div>

            {!!avatar && (
              <div className="w-fit">
                <Button variant="plain" disabled={isLoading} onClick={handleRemoveAvatar}>
                  Remove
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      {!isInvestor && (
        <div className="flex flex-col gap-6">
          <span className="font-semibold font-[Inter]">Event logs</span>
          <Toggle
            title="Enable event logs"
            enabled={!!currentUser?.notifications_enabled}
            disabled={isLoading}
            onChange={handleToggleNotifications}
            variant={'blue'}
          />
        </div>
      )}
      <div className="flex flex-col gap-6">
        <span className="font-semibold font-[Inter]">2FA Settings</span>

        <div className="flex gap-6 items-center justify-between w-full">
          <TooltipNew
            text={`Please talk to your administrator to turn ${isEnable2FA ? 'off' : 'on'} 2FA`}
            disabled={!get2FADisabledState(!!isEnable2FA)}
          >
            <Toggle
              title="Require 2FA on each login via password"
              enabled={!!isEnable2FA}
              disabled={
                get2FADisabledState(!!isEnable2FA) ||
                isLoadingToggle2FA ||
                isLoadingReset2FA ||
                isToggle2FA
              }
              onChange={handleToggle2FA}
              variant={'blue'}
            />
          </TooltipNew>

          {isEnable2FA === true && (
            <div className="w-fit">
              <Button
                variant="plain"
                disabled={isLoadingToggle2FA || isLoadingReset2FA || isToggle2FA}
                loading={isLoadingReset2FA}
                onClick={handleReset2fa}
              >
                Reset
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
